import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid2,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFnsJalali } from "@mui/x-date-pickers/AdapterDateFnsJalaliV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns-jalali";
import axiosInstance from "../../../../api/axiosConfig";
import { useSession } from "../../../../context/SessionContext";

const cacheRtl = createCache({
  key: "adapter-date-fns-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

function AddScheduleModal({ open, onClose, trainingCampId, onSuccess }) {
  const [formData, setFormData] = useState({
    programDate: null, // Selected date
    programDescription: "",
    programStartTime: "",
    programEndTime: "",
  });

  const { session } = useSession(); // Get session context
  const userId = session?.user?.id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleDateChange = (newDate) => {
    setFormData({ ...formData, programDate: newDate });
  };

  const handleSubmit = async () => {
    if (
      !formData.programDate ||
      !formData.programDescription ||
      !formData.programStartTime ||
      !formData.programEndTime
    ) {
      setError("لطفاً تمام فیلدهای مورد نیاز را پر کنید.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Convert date to Jalali format (YYYY/MM/DD)
      const jalaliDate = format(formData.programDate, "yyyy/MM/dd");

      await axiosInstance.post(
        `/Training/api/Training/CreateTrainingSchedule`,
        {
          trainingCampId,
          programDate: jalaliDate, // Send date in Jalali format
          programDescription: formData.programDescription,
          programStartTime: formData.programStartTime,
          programEndTime: formData.programEndTime,
          userId,
        },
      );

      onSuccess(); // Notify parent to refresh schedules
      onClose(); // Close modal
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در ذخیره برنامه تمرینی.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="add-schedule-modal">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "800px",
          bgcolor: "background.paper",
          border: "1px solid #ccc",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          direction: "rtl",
        }}
      >
        {/* Modal Header */}
        <Grid2
          container
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography id="add-schedule-modal" variant="h6">
            افزودن برنامه تمرینی جدید
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Grid2>

        {loading ? (
          <CircularProgress
            sx={{ display: "block", margin: "0 auto", mt: 4 }}
          />
        ) : (
          <>
            {/* Error Message */}
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {/* Form Fields */}
            <Grid2 container spacing={2}>
              {/* Date Field */}
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
                      <DatePicker
                        label="روز برنامه"
                        value={formData.programDate}
                        onChange={handleDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                </CacheProvider>
              </Grid2>

              {/* Description */}
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      label="عنوان برنامه"
                      placeholder="مثلاً تمرین روز اول"
                      variant="outlined"
                      value={formData.programDescription}
                      onChange={handleChange("programDescription")}
                    />
                  </div>
                </CacheProvider>
              </Grid2>

              {/* Start Time */}
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      type="time"
                      label="ساعت شروع"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={formData.programStartTime}
                      onChange={handleChange("programStartTime")}
                    />
                  </div>
                </CacheProvider>
              </Grid2>

              {/* End Time */}
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      type="time"
                      label="ساعت پایان"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={formData.programEndTime}
                      onChange={handleChange("programEndTime")}
                    />
                  </div>
                </CacheProvider>
              </Grid2>
            </Grid2>

            {/* Actions */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button variant="outlined" onClick={onClose}>
                انصراف
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "در حال ذخیره..." : "ذخیره"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default AddScheduleModal;
